import { __ } from '@wordpress/i18n';
import { fetchGraphQL } from './api';

const REST_BASE = '/wp-json/anacrm/v1';

const ORGS_SEARCH_QUERY = /* GraphQL */ `
	query SearchOrgsForMembership($search: String) {
		orgs(
			first: 20
			where: { search: $search, orderby: { field: TITLE, order: ASC } }
		) {
			nodes {
				databaseId
				title
			}
		}
	}
`;

const ADMINS_SEARCH_QUERY = /* GraphQL */ `
	query SearchAdminsForMembership($search: String) {
		administrations(
			first: 20
			where: { search: $search, orderby: { field: TITLE, order: ASC } }
		) {
			nodes {
				databaseId
				title
			}
		}
	}
`;

const PEOPLE_SEARCH_QUERY = /* GraphQL */ `
	query SearchPeopleForMembership($search: String) {
		people(
			first: 20
			where: { search: $search, orderby: { field: TITLE, order: ASC } }
		) {
			nodes {
				databaseId
				title
			}
		}
	}
`;

function nonce() {
	return window.anacrm?.nonce ?? '';
}

async function apiFetch( url, options = {} ) {
	const res = await fetch( url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce(),
			...( options.headers ?? {} ),
		},
	} );
	if ( res.status === 204 ) {
		return null;
	}
	const body = await res.json().catch( () => ( {} ) );
	if ( ! res.ok ) {
		// eslint-disable-next-line no-console
		console.error( '[membership] apiFetch error', res.status, url, body );
		throw new Error( body?.message ?? `HTTP ${ res.status }` );
	}
	return body;
}

async function searchOrgs( search ) {
	const data = await fetchGraphQL( ORGS_SEARCH_QUERY, {
		search: search ?? '',
	} );
	return data.orgs.nodes.map( ( n ) => ( {
		id: n.databaseId,
		name: n.title,
	} ) );
}

async function searchAdministrations( search ) {
	const data = await fetchGraphQL( ADMINS_SEARCH_QUERY, {
		search: search ?? '',
	} );
	return data.administrations.nodes.map( ( n ) => ( {
		id: n.databaseId,
		name: n.title,
	} ) );
}

async function searchPeople( search ) {
	const data = await fetchGraphQL( PEOPLE_SEARCH_QUERY, {
		search: search ?? '',
	} );
	return data.people.nodes.map( ( n ) => ( {
		id: n.databaseId,
		name: n.title,
	} ) );
}

async function loadPositions() {
	// Try the standard WP REST taxonomy endpoint first.
	const res = await fetch(
		'/wp-json/wp/v2/position?per_page=100&_fields=id,name',
		{
			headers: { 'X-WP-Nonce': nonce() },
		}
	);
	if ( res.ok ) {
		const data = await res.json();
		return data.map( ( t ) => ( { id: t.id, name: t.name } ) );
	}
	// Fallback: custom anacrm endpoint.
	const fallback = await apiFetch( `${ REST_BASE }/positions` );
	return Array.isArray( fallback ) ? fallback : [];
}

/**
 * Autocomplete widget for the position taxonomy field.
 * Filters existing terms as the user types and offers a "Create" option
 * when the typed text has no exact match.
 *
 * Returns an object with:
 *   el        — the DOM wrapper to append to the form
 *   getValue  — () => selected term ID (integer) or null
 *   getName   — () => selected term name string
 *   reset     — () => clears the field
 *
 * The `positions` array is mutated in-place when a new term is created so
 * subsequent opens of the dropdown include the new term.
 * @param positions
 * @param placeholder
 */
function buildPositionAutocomplete( positions, placeholder ) {
	let selectedId = null;
	let selectedName = '';

	const wrapper = document.createElement( 'div' );
	wrapper.className = 'relative flex-1 min-w-0';

	const input = document.createElement( 'input' );
	input.type = 'text';
	input.placeholder = placeholder;
	input.className = 'border border-gray-300 rounded px-2 py-1 text-sm w-full';
	input.autocomplete = 'off';

	const dropdown = document.createElement( 'ul' );
	dropdown.className = 'bg-white border border-gray-200 rounded shadow-lg';
	dropdown.style.cssText =
		'display:none; position:fixed; max-height:12rem; overflow-y:auto; z-index:99999; margin:0; padding:0; list-style:none;';

	// Portal to body so no ancestor stacking context clips the z-index.
	document.body.appendChild( dropdown );

	function positionDropdown() {
		const rect = input.getBoundingClientRect();
		dropdown.style.top = rect.bottom + 'px';
		dropdown.style.left = rect.left + 'px';
		dropdown.style.width = rect.width + 'px';
	}

	function openDropdown() {
		positionDropdown();
		dropdown.style.display = 'block';
		// Capture-phase so scrolling inside any ancestor container (which
		// doesn't bubble) still repositions the body-portaled dropdown.
		window.addEventListener( 'scroll', positionDropdown, true );
		window.addEventListener( 'resize', positionDropdown );
	}

	function closeDropdown() {
		dropdown.style.display = 'none';
		window.removeEventListener( 'scroll', positionDropdown, true );
		window.removeEventListener( 'resize', positionDropdown );
	}

	wrapper.appendChild( input );
	// dropdown is on body — do NOT append to wrapper.

	function renderDropdown( query ) {
		dropdown.innerHTML = '';
		const q = query.toLowerCase().trim();
		const filtered = q
			? positions.filter( ( p ) => p.name.toLowerCase().includes( q ) )
			: positions;

		for ( const pos of filtered ) {
			const li = document.createElement( 'li' );
			li.className =
				'px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-100 list-none';
			li.textContent = pos.name;
			li.addEventListener( 'mousedown', ( e ) => {
				e.preventDefault();
				selectedId = pos.id;
				selectedName = pos.name;
				input.value = pos.name;
				closeDropdown();
			} );
			dropdown.appendChild( li );
		}

		const exactMatch = positions.some(
			( p ) => p.name.toLowerCase() === q
		);
		if ( q && ! exactMatch ) {
			const li = document.createElement( 'li' );
			li.className =
				'px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 text-blue-600 font-medium border-t border-gray-100 list-none';
			li.textContent = __( 'Create', 'anacrm' ) + ' "' + query + '"';
			li.addEventListener( 'mousedown', async ( e ) => {
				e.preventDefault();
				closeDropdown();
				input.disabled = true;
				try {
					const created = await apiFetch(
						`${ REST_BASE }/positions`,
						{
							method: 'POST',
							body: JSON.stringify( { name: query } ),
						}
					);
					const newTerm = {
						id: created.id,
						name: created.name ?? query,
					};
					positions.push( newTerm );
					selectedId = newTerm.id;
					selectedName = newTerm.name;
					input.value = newTerm.name;
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error(
						'[membership] failed to create position',
						err
					);
					selectedId = null;
					selectedName = '';
					input.value = '';
				} finally {
					input.disabled = false;
					input.focus();
				}
			} );
			dropdown.appendChild( li );
		}

		if ( dropdown.children.length ) {
			openDropdown();
		} else {
			closeDropdown();
		}
	}

	input.addEventListener( 'focus', () => renderDropdown( input.value ) );
	input.addEventListener( 'input', () => {
		selectedId = null;
		selectedName = '';
		renderDropdown( input.value );
	} );
	input.addEventListener( 'blur', () => {
		// Delay so mousedown on a list item fires before the blur clears the field.
		setTimeout( () => {
			closeDropdown();
			if ( ! selectedId ) {
				input.value = '';
				selectedName = '';
			}
		}, 200 );
	} );

	return {
		el: wrapper,
		getValue: () => selectedId,
		getName: () => selectedName,
		setValue( id, name ) {
			selectedId = id || null;
			selectedName = name || '';
			input.value = selectedName;
		},
		reset() {
			selectedId = null;
			selectedName = '';
			input.value = '';
			closeDropdown();
		},
	};
}

/**
 * Autocomplete widget for a membership entity field (person, org, or
 * administration). Same shell as buildPositionAutocomplete() (portaled
 * dropdown, focus/input/blur wiring), but searches server-side (debounced)
 * via the given `searchFn` instead of filtering an in-memory array, since
 * the full entity list can be far larger than what a single GraphQL page
 * (or a `<select>`) can reasonably hold.
 *
 * Returns an object with:
 *   el        — the DOM wrapper to append to the form
 *   getValue  — () => selected entity ID (integer) or null
 *   getName   — () => selected entity name string
 *   setValue  — (id, name) => pre-fill a known selection (e.g. when editing)
 *   reset     — () => clears the field
 * @param {string}                              placeholder
 * @param {(search: string) => Promise<Object>} searchFn    Resolves to an
 *                                                          array of { id, name }.
 */
function buildSearchAutocomplete( placeholder, searchFn ) {
	let selectedId = null;
	let selectedName = '';
	let requestSeq = 0;

	const wrapper = document.createElement( 'div' );
	wrapper.className = 'relative flex-1 min-w-0';

	const input = document.createElement( 'input' );
	input.type = 'text';
	input.placeholder = placeholder;
	input.className = 'border border-gray-300 rounded px-2 py-1 text-sm w-full';
	input.autocomplete = 'off';

	const dropdown = document.createElement( 'ul' );
	dropdown.className = 'bg-white border border-gray-200 rounded shadow-lg';
	dropdown.style.cssText =
		'display:none; position:fixed; max-height:12rem; overflow-y:auto; z-index:99999; margin:0; padding:0; list-style:none;';

	// Portal to body so no ancestor stacking context clips the z-index.
	document.body.appendChild( dropdown );

	function positionDropdown() {
		const rect = input.getBoundingClientRect();
		dropdown.style.top = rect.bottom + 'px';
		dropdown.style.left = rect.left + 'px';
		dropdown.style.width = rect.width + 'px';
	}

	function openDropdown() {
		positionDropdown();
		dropdown.style.display = 'block';
		// Capture-phase so scrolling inside any ancestor container (which
		// doesn't bubble) still repositions the body-portaled dropdown.
		window.addEventListener( 'scroll', positionDropdown, true );
		window.addEventListener( 'resize', positionDropdown );
	}

	function closeDropdown() {
		dropdown.style.display = 'none';
		window.removeEventListener( 'scroll', positionDropdown, true );
		window.removeEventListener( 'resize', positionDropdown );
	}

	wrapper.appendChild( input );
	// dropdown is on body — do NOT append to wrapper.

	function renderMessage( text ) {
		dropdown.innerHTML = '';
		const li = document.createElement( 'li' );
		li.className = 'px-3 py-1.5 text-sm text-gray-500 list-none';
		li.textContent = text;
		dropdown.appendChild( li );
		openDropdown();
	}

	function renderResults( items ) {
		dropdown.innerHTML = '';
		if ( ! items.length ) {
			renderMessage( __( 'No matches', 'anacrm' ) );
			return;
		}
		for ( const item of items ) {
			const li = document.createElement( 'li' );
			li.className =
				'px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-100 list-none';
			li.textContent = item.name;
			li.addEventListener( 'mousedown', ( e ) => {
				e.preventDefault();
				selectedId = item.id;
				selectedName = item.name;
				input.value = item.name;
				closeDropdown();
			} );
			dropdown.appendChild( li );
		}
		openDropdown();
	}

	async function search( query ) {
		const seq = ++requestSeq;
		renderMessage( __( 'Loading…', 'anacrm' ) );
		try {
			const items = await searchFn( query );
			if ( seq !== requestSeq ) {
				return; // A newer request has since been fired — discard this one.
			}
			renderResults( items );
		} catch ( err ) {
			if ( seq !== requestSeq ) {
				return;
			}
			// eslint-disable-next-line no-console
			console.error( '[membership] search failed', err );
			renderMessage( __( 'Could not load results.', 'anacrm' ) );
		}
	}

	let debounceTimer = null;
	function scheduleSearch( query ) {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( () => search( query ), 300 );
	}

	input.addEventListener( 'focus', () => {
		if ( ! selectedId ) {
			scheduleSearch( input.value );
		}
	} );
	input.addEventListener( 'input', () => {
		selectedId = null;
		selectedName = '';
		scheduleSearch( input.value );
	} );
	input.addEventListener( 'blur', () => {
		// Delay so mousedown on a list item fires before the blur clears the field.
		setTimeout( () => {
			clearTimeout( debounceTimer );
			closeDropdown();
			if ( ! selectedId ) {
				input.value = '';
				selectedName = '';
			}
		}, 200 );
	} );

	return {
		el: wrapper,
		getValue: () => selectedId,
		getName: () => selectedName,
		setValue( id, name ) {
			selectedId = id || null;
			selectedName = name || '';
			input.value = selectedName;
		},
		reset() {
			selectedId = null;
			selectedName = '';
			input.value = '';
			clearTimeout( debounceTimer );
			closeDropdown();
		},
	};
}

function membershipLabel( membership, entityNameKey ) {
	return (
		( membership[ entityNameKey ] || __( '(none)', 'anacrm' ) ) +
		' — ' +
		( membership.positionName || __( '(no position)', 'anacrm' ) )
	);
}

function renderRow( container, membership, entityNameKey, onDelete, onEdit ) {
	const row = document.createElement( 'div' );
	row.className = 'flex items-center gap-2 py-1 membership-row';
	row.dataset.id = membership.id ?? '';

	const label = document.createElement( 'span' );
	label.className = 'flex-1 text-sm';
	label.textContent = membershipLabel( membership, entityNameKey );

	const edit = document.createElement( 'button' );
	edit.type = 'button';
	edit.className =
		'px-2 py-0.5 text-sm text-gray-600 hover:text-gray-900 cursor-pointer shrink-0';
	edit.textContent = __( 'Edit', 'anacrm' );
	edit.addEventListener( 'click', () => onEdit( row, label, membership ) );

	const del = document.createElement( 'button' );
	del.type = 'button';
	del.className =
		'px-2 py-0.5 text-sm text-red-600 hover:text-red-800 cursor-pointer shrink-0';
	del.textContent = __( 'Remove', 'anacrm' );
	del.addEventListener( 'click', () => onDelete( row, membership ) );

	row.appendChild( label );
	row.appendChild( edit );
	row.appendChild( del );
	container.appendChild( row );
}

/**
 * Mount the membership repeater UI into `container`.
 *
 * @param {HTMLElement} container Mount point (inside the form).
 * @param {string}      personId  Post ID string, empty for new-person form.
 * @param {boolean}     isNew     True when creating a new person.
 * @param {Object}      opts
 * @param {string}      opts.type 'org' (default) or 'administration'.
 */
export async function initMembershipSection(
	container,
	personId,
	isNew,
	opts = {}
) {
	const type = opts.type ?? 'org';
	const isOrg = type === 'org';

	// Keys used in REST payloads and row display.
	const entityIdKey = isOrg ? 'org_id' : 'administration_id';
	const entityIdKeyCamel = isOrg ? 'orgId' : 'administrationId';
	const entityNameKey = isOrg ? 'orgName' : 'administrationName';

	// GET responses use camelCase ids; pending (not-yet-persisted) entries use
	// the snake_case REST payload keys — normalise reads across both shapes.
	const extractEntityId = ( m ) =>
		m[ entityIdKey ] ?? m[ entityIdKeyCamel ] ?? null;
	const extractPositionId = ( m ) => m.position_id ?? m.positionId ?? null;

	const entityPlaceholder = isOrg
		? __( '— Organisation —', 'anacrm' )
		: __( '— Administration —', 'anacrm' );

	// Pending list for new-person form (not yet persisted).
	let pending = [];

	// Hidden input that carries pending memberships through the Pods form submit.
	// Separate names per type so both are included in the AJAX payload.
	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = isOrg
		? 'anacrm_pending_memberships'
		: 'anacrm_pending_memberships_ad';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	// List of confirmed rows.
	const list = document.createElement( 'div' );
	list.className = 'membership-list mb-3 space-y-1';
	container.appendChild( list );

	const searchEntities = isOrg ? searchOrgs : searchAdministrations;

	let positions = [];
	try {
		positions = await loadPositions();
	} catch ( err ) {
		const errMsg = document.createElement( 'p' );
		errMsg.className = 'text-sm text-red-600';
		errMsg.textContent = __( 'Could not load positions.', 'anacrm' );
		container.appendChild( errMsg );
		return;
	}

	// Load existing memberships for edit form.
	if ( ! isNew && personId ) {
		try {
			const items = await apiFetch(
				`${ REST_BASE }/person/${ personId }/memberships?type=${ type }`
			);
			if ( Array.isArray( items ) ) {
				for ( const m of items ) {
					renderRow(
						list,
						m,
						entityNameKey,
						handleDelete,
						startEdit
					);
				}
			}
		} catch {
			// Non-fatal — show empty list.
		}
	}

	// Add-row area.
	const addArea = document.createElement( 'div' );
	addArea.className = 'membership-add-area';
	container.appendChild( addArea );

	const addBtn = document.createElement( 'button' );
	addBtn.type = 'button';
	addBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	addBtn.textContent = isOrg
		? __( '+ Add organisation membership', 'anacrm' )
		: __( '+ Add administration membership', 'anacrm' );
	addArea.appendChild( addBtn );

	// Inline add form (hidden by default).
	const addForm = document.createElement( 'div' );
	addForm.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end membership-add-form';
	addArea.appendChild( addForm );

	const entityAC = buildSearchAutocomplete(
		entityPlaceholder,
		searchEntities
	);
	const posAC = buildPositionAutocomplete(
		positions,
		__( '— Position —', 'anacrm' )
	);

	const saveBtn = document.createElement( 'button' );
	saveBtn.type = 'button';
	saveBtn.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	saveBtn.textContent = __( 'Save', 'anacrm' );

	const cancelBtn = document.createElement( 'button' );
	cancelBtn.type = 'button';
	cancelBtn.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	cancelBtn.textContent = __( 'Cancel', 'anacrm' );

	const errEl = document.createElement( 'p' );
	errEl.className = 'w-full text-xs text-red-600 hidden';

	addForm.appendChild( entityAC.el );
	addForm.appendChild( posAC.el );
	addForm.appendChild( saveBtn );
	addForm.appendChild( cancelBtn );
	addForm.appendChild( errEl );

	// Non-null while an existing row (persisted or pending) is being edited
	// in place, rather than a brand new membership being added.
	let editing = null;

	function startEdit( row, label, membership ) {
		editing = { row, label, membership };
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		entityAC.setValue(
			extractEntityId( membership ),
			membership[ entityNameKey ]
		);
		posAC.setValue(
			extractPositionId( membership ),
			membership.positionName
		);
		errEl.classList.add( 'hidden' );
		saveBtn.textContent = __( 'Update', 'anacrm' );
	}

	function stopEdit() {
		editing = null;
		entityAC.reset();
		posAC.reset();
		addForm.classList.add( 'hidden' );
		addBtn.classList.remove( 'hidden' );
		saveBtn.textContent = __( 'Save', 'anacrm' );
	}

	addBtn.addEventListener( 'click', () => {
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		entityAC.reset();
		posAC.reset();
		errEl.classList.add( 'hidden' );
	} );

	cancelBtn.addEventListener( 'click', stopEdit );

	saveBtn.addEventListener( 'click', async () => {
		const entityId = entityAC.getValue();

		if ( ! entityId ) {
			errEl.textContent = isOrg
				? __( 'Please select an organisation.', 'anacrm' )
				: __( 'Please select an administration.', 'anacrm' );
			errEl.classList.remove( 'hidden' );
			return;
		}

		const posId = posAC.getValue();

		errEl.classList.add( 'hidden' );
		saveBtn.disabled = true;

		const entityName = entityAC.getName();
		const posName = posAC.getName();

		if ( editing ) {
			const { label, membership } = editing;
			if ( membership.id ) {
				const payload = {
					person_id: parseInt( personId, 10 ),
					[ entityIdKey ]: entityId,
					position_id: posId,
				};
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/memberships/${ membership.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( payload ),
						}
					);
					label.textContent = membershipLabel(
						updated,
						entityNameKey
					);
					stopEdit();
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error( '[membership] update failed', err );
					errEl.textContent =
						err.message ||
						__(
							'Could not update membership. Please try again.',
							'anacrm'
						);
					errEl.classList.remove( 'hidden' );
				} finally {
					saveBtn.disabled = false;
				}
			} else {
				membership[ entityIdKey ] = entityId;
				membership.position_id = posId;
				membership[ entityNameKey ] = entityName;
				membership.positionName = posName;
				syncHidden();
				label.textContent = membershipLabel(
					membership,
					entityNameKey
				);
				stopEdit();
				saveBtn.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				[ entityIdKey ]: entityId,
				position_id: posId,
				[ entityNameKey ]: entityName,
				positionName: posName,
			};
			pending.push( entry );
			syncHidden();

			renderRow(
				list,
				entry,
				entityNameKey,
				handleDeletePending,
				startEdit
			);

			addForm.classList.add( 'hidden' );
			addBtn.classList.remove( 'hidden' );
			saveBtn.disabled = false;
		} else {
			const payload = {
				person_id: parseInt( personId, 10 ),
				[ entityIdKey ]: entityId,
				position_id: posId,
			};
			try {
				const created = await apiFetch( `${ REST_BASE }/memberships`, {
					method: 'POST',
					body: JSON.stringify( payload ),
				} );

				renderRow(
					list,
					created,
					entityNameKey,
					handleDelete,
					startEdit
				);

				addForm.classList.add( 'hidden' );
				addBtn.classList.remove( 'hidden' );
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( '[membership] save failed', err );
				errEl.textContent =
					err.message ||
					__(
						'Could not save membership. Please try again.',
						'anacrm'
					);
				errEl.classList.remove( 'hidden' );
			} finally {
				saveBtn.disabled = false;
			}
		}
	} );

	function handleDelete( row, membership ) {
		if ( ! membership.id ) {
			return;
		}
		apiFetch( `${ REST_BASE }/memberships/${ membership.id }`, {
			method: 'DELETE',
		} )
			.then( () => row.remove() )
			.catch( () => {
				const e = document.createElement( 'span' );
				e.className = 'text-xs text-red-600 ml-1';
				e.textContent = __( 'Error', 'anacrm' );
				row.appendChild( e );
			} );
	}

	function handleDeletePending( row, membership ) {
		pending = pending.filter( ( m ) => m !== membership );
		syncHidden();
		row.remove();
	}
}

/**
 * Mount the membership repeater UI into `container` from the org side.
 * Lets the user pick a person + position for this org.
 *
 * @param {HTMLElement} container Mount point (inside the org form).
 * @param {string}      orgId     Post ID string, empty for new-org form.
 * @param {boolean}     isNew     True when creating a new org.
 */
export async function initOrgMembershipSection( container, orgId, isNew ) {
	const entityNameKey = 'personName';

	// GET responses use camelCase ids; pending (not-yet-persisted) entries use
	// the snake_case REST payload keys — normalise reads across both shapes.
	const extractPersonId = ( m ) => m.person_id ?? m.personId ?? null;
	const extractPositionId = ( m ) => m.position_id ?? m.positionId ?? null;

	// Pending list for new-org form (not yet persisted).
	let pending = [];

	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = 'anacrm_pending_memberships_pe';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	const list = document.createElement( 'div' );
	list.className = 'membership-list mb-3 space-y-1';
	container.appendChild( list );

	let positions = [];
	try {
		positions = await loadPositions();
	} catch ( err ) {
		const errMsg = document.createElement( 'p' );
		errMsg.className = 'text-sm text-red-600';
		errMsg.textContent = __( 'Could not load positions.', 'anacrm' );
		container.appendChild( errMsg );
		return;
	}

	if ( ! isNew && orgId ) {
		try {
			const items = await apiFetch(
				`${ REST_BASE }/org/${ orgId }/memberships`
			);
			if ( Array.isArray( items ) ) {
				for ( const m of items ) {
					renderRow(
						list,
						m,
						entityNameKey,
						handleDelete,
						startEdit
					);
				}
			}
		} catch {
			// Non-fatal — show empty list.
		}
	}

	const addArea = document.createElement( 'div' );
	addArea.className = 'membership-add-area';
	container.appendChild( addArea );

	const addBtn = document.createElement( 'button' );
	addBtn.type = 'button';
	addBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	addBtn.textContent = __( '+ Add associated person', 'anacrm' );
	addArea.appendChild( addBtn );

	const addForm = document.createElement( 'div' );
	addForm.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end membership-add-form';
	addArea.appendChild( addForm );

	const personAC = buildSearchAutocomplete(
		__( '— Person —', 'anacrm' ),
		searchPeople
	);
	const posAC = buildPositionAutocomplete(
		positions,
		__( '— Position —', 'anacrm' )
	);

	const saveBtn = document.createElement( 'button' );
	saveBtn.type = 'button';
	saveBtn.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	saveBtn.textContent = __( 'Save', 'anacrm' );

	const cancelBtn = document.createElement( 'button' );
	cancelBtn.type = 'button';
	cancelBtn.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	cancelBtn.textContent = __( 'Cancel', 'anacrm' );

	const errEl = document.createElement( 'p' );
	errEl.className = 'w-full text-xs text-red-600 hidden';

	addForm.appendChild( personAC.el );
	addForm.appendChild( posAC.el );
	addForm.appendChild( saveBtn );
	addForm.appendChild( cancelBtn );
	addForm.appendChild( errEl );

	// Non-null while an existing row (persisted or pending) is being edited
	// in place, rather than a brand new membership being added.
	let editing = null;

	function startEdit( row, label, membership ) {
		editing = { row, label, membership };
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		personAC.setValue(
			extractPersonId( membership ),
			membership.personName
		);
		posAC.setValue(
			extractPositionId( membership ),
			membership.positionName
		);
		errEl.classList.add( 'hidden' );
		saveBtn.textContent = __( 'Update', 'anacrm' );
	}

	function stopEdit() {
		editing = null;
		personAC.reset();
		posAC.reset();
		addForm.classList.add( 'hidden' );
		addBtn.classList.remove( 'hidden' );
		saveBtn.textContent = __( 'Save', 'anacrm' );
	}

	addBtn.addEventListener( 'click', () => {
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		personAC.reset();
		posAC.reset();
		errEl.classList.add( 'hidden' );
	} );

	cancelBtn.addEventListener( 'click', stopEdit );

	saveBtn.addEventListener( 'click', async () => {
		const personId = personAC.getValue();

		if ( ! personId ) {
			errEl.textContent = __( 'Please select a person.', 'anacrm' );
			errEl.classList.remove( 'hidden' );
			return;
		}

		const posId = posAC.getValue();

		errEl.classList.add( 'hidden' );
		saveBtn.disabled = true;

		const personName = personAC.getName();
		const posName = posAC.getName();

		if ( editing ) {
			const { label, membership } = editing;
			if ( membership.id ) {
				const payload = {
					person_id: personId,
					org_id: parseInt( orgId, 10 ),
					position_id: posId,
				};
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/memberships/${ membership.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( payload ),
						}
					);
					label.textContent = membershipLabel(
						updated,
						entityNameKey
					);
					stopEdit();
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error( '[membership] update failed', err );
					errEl.textContent =
						err.message ||
						__(
							'Could not update membership. Please try again.',
							'anacrm'
						);
					errEl.classList.remove( 'hidden' );
				} finally {
					saveBtn.disabled = false;
				}
			} else {
				membership.person_id = personId;
				membership.position_id = posId;
				membership.personName = personName;
				membership.positionName = posName;
				syncHidden();
				label.textContent = membershipLabel(
					membership,
					entityNameKey
				);
				stopEdit();
				saveBtn.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				person_id: personId,
				position_id: posId,
				personName,
				positionName: posName,
			};
			pending.push( entry );
			syncHidden();

			renderRow(
				list,
				entry,
				entityNameKey,
				handleDeletePending,
				startEdit
			);

			addForm.classList.add( 'hidden' );
			addBtn.classList.remove( 'hidden' );
			saveBtn.disabled = false;
		} else {
			const payload = {
				person_id: personId,
				org_id: parseInt( orgId, 10 ),
				position_id: posId,
			};
			try {
				const created = await apiFetch( `${ REST_BASE }/memberships`, {
					method: 'POST',
					body: JSON.stringify( payload ),
				} );

				renderRow(
					list,
					created,
					entityNameKey,
					handleDelete,
					startEdit
				);

				addForm.classList.add( 'hidden' );
				addBtn.classList.remove( 'hidden' );
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( '[membership] save failed', err );
				errEl.textContent =
					err.message ||
					__(
						'Could not save membership. Please try again.',
						'anacrm'
					);
				errEl.classList.remove( 'hidden' );
			} finally {
				saveBtn.disabled = false;
			}
		}
	} );

	function handleDelete( row, membership ) {
		if ( ! membership.id ) {
			return;
		}
		apiFetch( `${ REST_BASE }/memberships/${ membership.id }`, {
			method: 'DELETE',
		} )
			.then( () => row.remove() )
			.catch( () => {
				const e = document.createElement( 'span' );
				e.className = 'text-xs text-red-600 ml-1';
				e.textContent = __( 'Error', 'anacrm' );
				row.appendChild( e );
			} );
	}

	function handleDeletePending( row, membership ) {
		pending = pending.filter( ( m ) => m !== membership );
		syncHidden();
		row.remove();
	}
}

/**
 * Mount the membership repeater UI into `container` from the administration side.
 * Lets the user pick a person + position for this administration.
 *
 * @param {HTMLElement} container        Mount point (inside the administration form).
 * @param {string}      administrationId Post ID string, empty for new-administration form.
 * @param {boolean}     isNew            True when creating a new administration.
 */
export async function initAdministrationMembershipSection(
	container,
	administrationId,
	isNew
) {
	const entityNameKey = 'personName';

	// GET responses use camelCase ids; pending (not-yet-persisted) entries use
	// the snake_case REST payload keys — normalise reads across both shapes.
	const extractPersonId = ( m ) => m.person_id ?? m.personId ?? null;
	const extractPositionId = ( m ) => m.position_id ?? m.positionId ?? null;

	let pending = [];

	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = 'anacrm_pending_memberships_pe_ad';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	const list = document.createElement( 'div' );
	list.className = 'membership-list mb-3 space-y-1';
	container.appendChild( list );

	let positions = [];
	try {
		positions = await loadPositions();
	} catch ( err ) {
		const errMsg = document.createElement( 'p' );
		errMsg.className = 'text-sm text-red-600';
		errMsg.textContent = __( 'Could not load positions.', 'anacrm' );
		container.appendChild( errMsg );
		return;
	}

	if ( ! isNew && administrationId ) {
		try {
			const items = await apiFetch(
				`${ REST_BASE }/administration/${ administrationId }/memberships`
			);
			if ( Array.isArray( items ) ) {
				for ( const m of items ) {
					renderRow(
						list,
						m,
						entityNameKey,
						handleDelete,
						startEdit
					);
				}
			}
		} catch {
			// Non-fatal — show empty list.
		}
	}

	const addArea = document.createElement( 'div' );
	addArea.className = 'membership-add-area';
	container.appendChild( addArea );

	const addBtn = document.createElement( 'button' );
	addBtn.type = 'button';
	addBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	addBtn.textContent = __( '+ Add associated person', 'anacrm' );
	addArea.appendChild( addBtn );

	const addForm = document.createElement( 'div' );
	addForm.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end membership-add-form';
	addArea.appendChild( addForm );

	const personAC = buildSearchAutocomplete(
		__( '— Person —', 'anacrm' ),
		searchPeople
	);
	const posAC = buildPositionAutocomplete(
		positions,
		__( '— Position —', 'anacrm' )
	);

	const saveBtn = document.createElement( 'button' );
	saveBtn.type = 'button';
	saveBtn.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	saveBtn.textContent = __( 'Save', 'anacrm' );

	const cancelBtn = document.createElement( 'button' );
	cancelBtn.type = 'button';
	cancelBtn.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	cancelBtn.textContent = __( 'Cancel', 'anacrm' );

	const errEl = document.createElement( 'p' );
	errEl.className = 'w-full text-xs text-red-600 hidden';

	addForm.appendChild( personAC.el );
	addForm.appendChild( posAC.el );
	addForm.appendChild( saveBtn );
	addForm.appendChild( cancelBtn );
	addForm.appendChild( errEl );

	// Non-null while an existing row (persisted or pending) is being edited
	// in place, rather than a brand new membership being added.
	let editing = null;

	function startEdit( row, label, membership ) {
		editing = { row, label, membership };
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		personAC.setValue(
			extractPersonId( membership ),
			membership.personName
		);
		posAC.setValue(
			extractPositionId( membership ),
			membership.positionName
		);
		errEl.classList.add( 'hidden' );
		saveBtn.textContent = __( 'Update', 'anacrm' );
	}

	function stopEdit() {
		editing = null;
		personAC.reset();
		posAC.reset();
		addForm.classList.add( 'hidden' );
		addBtn.classList.remove( 'hidden' );
		saveBtn.textContent = __( 'Save', 'anacrm' );
	}

	addBtn.addEventListener( 'click', () => {
		addBtn.classList.add( 'hidden' );
		addForm.classList.remove( 'hidden' );
		personAC.reset();
		posAC.reset();
		errEl.classList.add( 'hidden' );
	} );

	cancelBtn.addEventListener( 'click', stopEdit );

	saveBtn.addEventListener( 'click', async () => {
		const personId = personAC.getValue();

		if ( ! personId ) {
			errEl.textContent = __( 'Please select a person.', 'anacrm' );
			errEl.classList.remove( 'hidden' );
			return;
		}

		const posId = posAC.getValue();

		errEl.classList.add( 'hidden' );
		saveBtn.disabled = true;

		const personName = personAC.getName();
		const posName = posAC.getName();

		if ( editing ) {
			const { label, membership } = editing;
			if ( membership.id ) {
				const payload = {
					person_id: personId,
					administration_id: parseInt( administrationId, 10 ),
					position_id: posId,
				};
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/memberships/${ membership.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( payload ),
						}
					);
					label.textContent = membershipLabel(
						updated,
						entityNameKey
					);
					stopEdit();
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error( '[membership] update failed', err );
					errEl.textContent =
						err.message ||
						__(
							'Could not update membership. Please try again.',
							'anacrm'
						);
					errEl.classList.remove( 'hidden' );
				} finally {
					saveBtn.disabled = false;
				}
			} else {
				membership.person_id = personId;
				membership.position_id = posId;
				membership.personName = personName;
				membership.positionName = posName;
				syncHidden();
				label.textContent = membershipLabel(
					membership,
					entityNameKey
				);
				stopEdit();
				saveBtn.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				person_id: personId,
				position_id: posId,
				personName,
				positionName: posName,
			};
			pending.push( entry );
			syncHidden();

			renderRow(
				list,
				entry,
				entityNameKey,
				handleDeletePending,
				startEdit
			);

			addForm.classList.add( 'hidden' );
			addBtn.classList.remove( 'hidden' );
			saveBtn.disabled = false;
		} else {
			const payload = {
				person_id: personId,
				administration_id: parseInt( administrationId, 10 ),
				position_id: posId,
			};
			try {
				const created = await apiFetch( `${ REST_BASE }/memberships`, {
					method: 'POST',
					body: JSON.stringify( payload ),
				} );

				renderRow(
					list,
					created,
					entityNameKey,
					handleDelete,
					startEdit
				);

				addForm.classList.add( 'hidden' );
				addBtn.classList.remove( 'hidden' );
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( '[membership] save failed', err );
				errEl.textContent =
					err.message ||
					__(
						'Could not save membership. Please try again.',
						'anacrm'
					);
				errEl.classList.remove( 'hidden' );
			} finally {
				saveBtn.disabled = false;
			}
		}
	} );

	function handleDelete( row, membership ) {
		if ( ! membership.id ) {
			return;
		}
		apiFetch( `${ REST_BASE }/memberships/${ membership.id }`, {
			method: 'DELETE',
		} )
			.then( () => row.remove() )
			.catch( () => {
				const e = document.createElement( 'span' );
				e.className = 'text-xs text-red-600 ml-1';
				e.textContent = __( 'Error', 'anacrm' );
				row.appendChild( e );
			} );
	}

	function handleDeletePending( row, membership ) {
		pending = pending.filter( ( m ) => m !== membership );
		syncHidden();
		row.remove();
	}
}
